// SideNav — left rail with pebble brand, route list, badges, mobile bottom-tab fallback.

import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Icon, type IconName } from "@/lib/icons";
import { useBadgeStore } from "@/store/badges";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: number | string;
}

const PRIMARY: NavItem[] = [
  { to: "/home", label: "Dashboard", icon: "Home" },
  { to: "/domains", label: "Domains", icon: "Folder" },
  { to: "/browse/instruments", label: "Browse", icon: "Database" },
  { to: "/approvals", label: "Approvals", icon: "Approve" },
  { to: "/lineage", label: "Lineage", icon: "Lineage" },
  { to: "/mappings", label: "Mappings", icon: "Map" },
  { to: "/quality", label: "Quality", icon: "Quality" },
  { to: "/imports", label: "Imports", icon: "Upload" },
  { to: "/hierarchies", label: "Hierarchies", icon: "Tree" },
];

const FOOTER: NavItem[] = [
  { to: "/integrations", label: "API & Webhooks", icon: "Api" },
  { to: "/audit", label: "Audit log", icon: "History" },
  { to: "/settings", label: "Settings", icon: "Settings" },
];

const BOTTOM_NAV: NavItem[] = [
  { to: "/home", label: "Home", icon: "Home" },
  { to: "/browse/instruments", label: "Browse", icon: "Database" },
  { to: "/approvals", label: "Review", icon: "Approve" },
  { to: "/settings", label: "Settings", icon: "Settings" },
];

function RailItem({ item }: { item: NavItem }) {
  const Glyph = Icon[item.icon];
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `lex-rail__item ${isActive ? "lex-rail__item--active" : ""}`}
    >
      {({ isActive }) => (
        <>
          <span className="lex-rail__icon" aria-hidden="true"><Glyph size={18} /></span>
          <span className="lex-rail__label">{item.label}</span>
          {item.badge != null ? (
            <span className="lex-rail__badge" aria-label={`${item.badge} ${item.label} pending`}>
              {item.badge}
            </span>
          ) : null}
          <span aria-current={isActive ? "page" : undefined} className="sr-only" />
        </>
      )}
    </NavLink>
  );
}

export function SideNav() {
  const approvalsPending = useBadgeStore(s => s.approvalsPending);
  const refresh = useBadgeStore(s => s.refreshApprovalsCount);
  const location = useLocation();

  // initial + on route change + 30s poll
  useEffect(() => { refresh(); }, [refresh, location.pathname]);
  useEffect(() => {
    const id = window.setInterval(() => refresh(), 30_000);
    const onFocus = () => refresh();
    const onMutation = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("aidan:mutation", onMutation);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("aidan:mutation", onMutation);
    };
  }, [refresh]);

  const primaryWithBadges = PRIMARY.map(item =>
    item.to === "/approvals" && approvalsPending > 0
      ? { ...item, badge: approvalsPending }
      : item
  );

  return (
    <>
      <nav className="lex-rail hide-mobile" aria-label="Primary">
        <div className="lex-rail__brand">
          <span className="lex-rail__pebble" aria-hidden="true" />
        </div>
        <ul className="lex-rail__nav" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {primaryWithBadges.map(item => (
            <li key={item.to}><RailItem item={item} /></li>
          ))}
        </ul>
        <ul className="lex-rail__foot" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {FOOTER.map(item => (
            <li key={item.to}><RailItem item={item} /></li>
          ))}
        </ul>
      </nav>

      <nav className="lex-bottom-nav show-mobile" aria-label="Primary mobile">
        {BOTTOM_NAV.map(item => {
          const Glyph = Icon[item.icon];
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `lex-bottom-nav__item ${isActive ? "lex-bottom-nav__item--active" : ""}`}
            >
              <Glyph size={20} aria-hidden="true" />
              <span className="lex-bottom-nav__label">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </>
  );
}

export default SideNav;
